import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BookOpen, Play } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import { PageLoadingState } from "components/layout/PageLoadingState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { useEventTracking } from "hooks/useEventTracking";
import useMyTrainingSummary from "hooks/useMyTrainingSummary";
import useFlowAssignments from "hooks/useFlowAssignments";
import { buildFlowPath } from "utils/flowNavigation";
import { learnerCourseComplete } from "utils/learnerCourses";
import { AssignedCourseSummary, CourseProgressFlow } from "types";
import { Activities } from "./Sections/Activities";
import { CourseLibrary } from "./CourseLibrary";
import { LessonThumbnail } from "./LessonThumbnail";
import { courseEligibility, courseUrl } from "./coursePresentation";
import "./MyTraining.css";

export const MyTraining: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const courseId = Number(params.get("courseId"));
  const quickWins = ["#quick-wins", "#minis"].includes(location.hash.toLowerCase());
  if (params.get("course") !== "life-plan" && !quickWins && Number.isSafeInteger(courseId) && courseId > 0) {
    return <Navigate to={courseUrl(courseId)} replace state={location.state} />;
  }
  return <CourseLibraryPage />;
};

function CourseLibraryPage() {
  const location = useLocation();
  const wantsLifePlan = new URLSearchParams(location.search).get("course") === "life-plan";
  const { data, loading, error, refetch } = useMyTrainingSummary();
  const assignments = useFlowAssignments();
  const navigate = useNavigate();
  const { track } = useEventTracking();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const courses = useMemo(() => data?.assigned_courses ?? [], [data]);
  const progress = useMemo(() => data?.all_course_progress ?? {}, [data]);
  const lifePlan = courses.find((course) => course.name.trim().toLowerCase() === "life plan");
  const quickWins = ["#quick-wins", "#minis"].includes(location.hash.toLowerCase());
  const activeTab = quickWins ? "quick-wins" : "courses";

  useEffect(() => {
    if (wantsLifePlan && lifePlan) navigate(courseUrl(lifePlan.id), { replace: true, state: location.state });
  }, [wantsLifePlan, lifePlan, navigate, location.state]);

  const launch = (course: AssignedCourseSummary, flow: CourseProgressFlow) => {
    track("button_click", { action: "continue_course_button", course_id: course.id, course_name: course.name });
    navigate(buildFlowPath({ courseId: course.id, flowName: flow.flow_details.name }));
  };
  const startCourse = (course: AssignedCourseSummary) => {
    const outline = progress[course.id];
    if (courseEligibility(course, outline)?.is_locked) { navigate(courseUrl(course.id)); return; }
    const flow = outline?.flows.find((item) => item.is_current && !item.is_locked);
    if (flow) launch(course, flow);
    else {
      track("button_click", { action: "continue_course_button", course_id: course.id, course_name: course.name });
      navigate(courseUrl(course.id), { state: { startCourse: true } });
    }
  };
  const ordered = [...courses].sort((a, b) => Number(b.id === data?.default_course_id) - Number(a.id === data?.default_course_id) || a.order - b.order);
  const resumeCourse = ordered.find((course) => !courseEligibility(course, progress[course.id])?.is_locked && !learnerCourseComplete(course, progress[course.id]) && progress[course.id]?.flows.some((flow) => flow.is_current && !flow.is_locked));
  const resumeProgress = resumeCourse ? progress[resumeCourse.id] : undefined;
  const resumeFlow = resumeProgress?.flows.find((flow) => flow.is_current && !flow.is_locked);
  const resumeChapter = resumeProgress?.chapters?.find((chapter) => chapter.id === resumeFlow?.chapter_id);
  const retry = () => { void refetch().catch(() => undefined); };
  if (loading && !data) return <PageLoadingState />;
  if (error && !data) return <section className="learner-courses course-empty" role="alert"><BookOpen size={36} /><h1>We couldn’t load your courses</h1><p>Please try again in a moment.</p><Button size="auto" onClick={retry}>Try again</Button></section>;
  return <section className="learner-courses learner-courses-simplified">
    <header className="course-page-heading"><div><div className="course-eyebrow">Your learning</div><h1>My Courses</h1><p className="course-muted">{courses.length} assigned · {courses.filter((course) => learnerCourseComplete(course, progress[course.id])).length} completed</p></div></header>
    {wantsLifePlan && !lifePlan && <p className="course-notice" role="status">Life Plan isn’t available in your courses yet. You can browse your available courses below.</p>}
    {!wantsLifePlan && <section className="course-resume course-resume-compact course-glass" aria-label="Resume learning">
      {resumeFlow ? <LessonThumbnail flow={resumeFlow} /> : <span className="course-resume-icon" aria-hidden="true"><BookOpen size={23} /></span>}
      <div className="course-resume-copy"><div className="course-eyebrow">{resumeCourse && resumeFlow ? "Pick up where you left off" : "Make space for your growth"}</div>
        <h2>{resumeFlow ? resumeFlow.flow_details.title || resumeFlow.flow_details.name : courses.length ? "Your learning, at your pace" : "Welcome to your learning journey"}</h2>
        <p>{resumeCourse ? `${resumeCourse.name}${resumeChapter ? ` · ${resumeChapter.title}` : ""}` : "Explore your courses below. Every small step is progress."}</p>
      </div>
      {resumeCourse && resumeFlow && <Button size="auto" onClick={() => launch(resumeCourse, resumeFlow)}><Play size={16} />{resumeFlow.has_started || (resumeFlow.response_count ?? 0) > 0 ? "Continue module" : "Start module"}</Button>}
    </section>}
    <div className="course-library-shell">
      <Tabs value={activeTab} onValueChange={(value) => navigate(`/training${location.search}#${value}`)}>
        <TabsList className="course-tabs"><TabsTrigger value="courses">Courses</TabsTrigger><TabsTrigger value="quick-wins">Quick Wins</TabsTrigger></TabsList>
        <TabsContent value="courses" className="mt-0" forceMount hidden={quickWins}>
          <CourseLibrary courses={courses} progress={progress} search={search} filter={filter} onSearch={setSearch} onFilter={setFilter} onStart={startCourse} onRetry={retry} courseErrors={data?.course_errors} />
        </TabsContent>
        <TabsContent value="quick-wins" className="mt-0"><div className="course-quick-wins"><Activities assignments={assignments.data} loading={assignments.loading} error={assignments.error} /></div></TabsContent>
      </Tabs>
    </div>
    {error && data && <div className="course-notice" role="alert">We couldn’t refresh your courses. <Button size="auto" variant="secondary" onClick={retry}>Try again</Button></div>}
    {courses.some((course) => courseEligibility(course, progress[course.id])?.is_locked) && <p className="course-locked-hint"><Link to={courseUrl(courses.find((course) => courseEligibility(course, progress[course.id])?.is_locked)!.id)}>View course requirements <ArrowRight size={14} aria-hidden="true" /></Link></p>}
  </section>;
}
